import {controller} from '@github/catalyst'

// Drag-and-drop reordering for a list of rows. Rows must carry
// `data-sortable-item` and `data-id`; a drag affordance inside each row must
// carry `data-sortable-handle`. On drop the new order of `data-id`s is PATCHed
// to `data-url` as `order[]`.
@controller
class SortableListElement extends HTMLElement {
  private dragging: HTMLElement | null = null

  get url(): string {
    return this.getAttribute('data-url') || ''
  }

  get csrfToken(): string {
    return (document.querySelector('meta[name="csrf-token"]') as HTMLMetaElement)?.content || ''
  }

  connectedCallback() {
    this.addEventListener('mousedown', this.onMouseDown)
    this.addEventListener('dragstart', this.onDragStart)
    this.addEventListener('dragover', this.onDragOver)
    this.addEventListener('drop', this.onDrop)
    this.addEventListener('dragend', this.onDragEnd)
  }

  private items(): HTMLElement[] {
    return Array.from(this.querySelectorAll<HTMLElement>('[data-sortable-item]'))
  }

  // Only allow a drag to begin from the handle, so inner links/buttons stay clickable.
  private onMouseDown = (e: MouseEvent) => {
    const handle = (e.target as HTMLElement).closest('[data-sortable-handle]')
    if (!handle) return
    const row = handle.closest<HTMLElement>('[data-sortable-item]')
    if (row) row.draggable = true
  }

  private onDragStart = (e: DragEvent) => {
    const row = (e.target as HTMLElement).closest<HTMLElement>('[data-sortable-item]')
    if (!row || !row.draggable) return
    this.dragging = row
    e.dataTransfer?.setData('text/plain', row.dataset.id || '')
    if (e.dataTransfer) e.dataTransfer.effectAllowed = 'move'
    row.classList.add('opacity-50')
  }

  private onDragOver = (e: DragEvent) => {
    if (!this.dragging) return
    e.preventDefault()
    if (e.dataTransfer) e.dataTransfer.dropEffect = 'move'

    const after = this.rowAfter(e.clientY)
    if (after == null) this.appendChild(this.dragging)
    else this.insertBefore(this.dragging, after)
  }

  private onDrop = (e: DragEvent) => {
    if (this.dragging) e.preventDefault()
  }

  private onDragEnd = () => {
    if (!this.dragging) return
    this.dragging.classList.remove('opacity-50')
    this.items().forEach((row) => (row.draggable = false))
    this.dragging = null
    void this.persist()
  }

  // The row before which the dragged row should be inserted, or null for the end.
  private rowAfter(y: number): HTMLElement | null {
    for (const row of this.items()) {
      if (row === this.dragging) continue
      const rect = row.getBoundingClientRect()
      if (y < rect.top + rect.height / 2) return row
    }
    return null
  }

  private async persist() {
    if (!this.url) return

    const params = new URLSearchParams()
    for (const row of this.items()) {
      if (row.dataset.id) params.append('order[]', row.dataset.id)
    }

    await fetch(this.url, {
      method: 'PATCH',
      headers: {
        'X-CSRF-Token': this.csrfToken,
        'Content-Type': 'application/x-www-form-urlencoded',
        'X-Requested-With': 'XMLHttpRequest',
      },
      body: params,
    })
  }
}
